import { useEffect, useState } from "react";
import axios from "axios";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Container,
  Chip,
  IconButton,
  Paper,
  styled,
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import BusinessIcon from "@mui/icons-material/Business";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Grid from '@mui/material/Grid';

import config from "../config.js";
import {
  setSelectedCompanyId,
  setSelectedCompanyDetails,
} from "../features/counter/kycSlice";


const CarouselContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  overflow: "hidden",
  padding: "0 60px",
  [theme.breakpoints.down("sm")]: {
    padding: "0 40px",
  },
}));

const StyledCompanyCard = styled(Card)(({ theme, selected }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  transition: "all 0.3s ease",
  border: selected
    ? `3px solid ${theme.palette.primary.main}`
    : "1px solid #e0e0e0",
  backgroundColor: selected
    ? "rgba(25, 118, 210, 0.04)"
    : theme.palette.background.paper,
  boxShadow: selected ? theme.shadows[8] : theme.shadows[2],
  position: "relative",
  "&:hover": {
    boxShadow: theme.shadows[6],
    transform: "translateY(-4px)",
  },
}));

const NavigationButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[3],
  zIndex: 2,
  "&:hover": {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  "&:disabled": {
    opacity: 0.3,
  },
}));

const InfoRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(1.5),
}));

const MyClientCompany = () => {
  const [kycData, setKycData] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const userId = useSelector((state) => state.auth.user?.id);
  const selectedCompanyId = useSelector((state) => state.kyc.selectedCompanyId);
  const { API_BASE_URL } = config;
  const activeClient = useSelector((state) => state.client?.activeClient);
  const selectedClient = useSelector((state) => state.client?.selectedClient);

  const ITEMS_PER_PAGE = 6; 
  const totalPages = Math.ceil(kycData.length / ITEMS_PER_PAGE);

  useEffect(() => {
    const fetchKycData = async () => {
      if (!userId) {
        setLoading(false);
        return;
      }

      const clientToUse = activeClient || selectedClient;

      if (!clientToUse) {
        setError("No client selected. Please select a client first.");
        setKycData([]);
        setLoading(false);
        return;
      }

      const clientUserId = clientToUse.userId;

      if (!clientUserId) {
        setError(
          "Invalid client data. Missing user ID. Please select a valid client."
        );
        setKycData([]);
        setLoading(false);
        return;
      }

      try {
        setError(null);
        setLoading(true);
        const response = await axios.get(
          `${API_BASE_URL}/api/v1/getKycData/${clientUserId}`
        );

        const fetchedData = response.data.kycData || [];
        setKycData(fetchedData);
      } catch (err) {
        console.error("Error fetching KYC data:", err);
        setError(
          `Failed to load company data: ${
            err.response?.data?.message || err.message
          }`
        );
        setKycData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchKycData();
  }, [userId, API_BASE_URL, activeClient, selectedClient]);

  const handleCompanySelect = (company) => {
    dispatch(setSelectedCompanyId(company._id));
    dispatch(
      setSelectedCompanyDetails({
        companyInfo: company.companyInfo,
        address: company.address,
        contactInfo: company.contactInfo,
        businessInfo: company.businessInfo,
        taxInfo: company.taxInfo,
        complianceInfo: company.complianceInfo,
        bankingInfo: company.bankingInfo,
      })
    );
  };

  const handlePrevious = () => {
    setCurrentPage((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1));
  };

  const handleSelectClient = () => {
    navigate("/ClientSelection");
  };

  const getCurrentPageCompanies = () => {
    const startIndex = currentPage * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return kycData.slice(startIndex, endIndex);
  };

  if (loading) {
    return (
    
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "60vh",
          }}
        >
          <CircularProgress size={60} />
        </Box>
      
    );
  }

  const clientToUse = activeClient || selectedClient;

  if (!clientToUse) {
    return (
     
        <Container maxWidth="md" sx={{ mt: 6, mb: 4 }}>
          <Paper
            elevation={3}
            sx={{
              p: 6,
              textAlign: "center",
              borderRadius: 3,
            }}
          >
            <PersonAddIcon
              sx={{ fontSize: 100, color: "primary.main", mb: 3 }}
            />
            <Typography variant="h4" gutterBottom fontWeight={600}>
              No Client Selected
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              mb={4}
              maxWidth={500}
              mx="auto"
            >
              To view and manage company information, please select a client
              from your client list.
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<PersonAddIcon />}
              onClick={handleSelectClient}
              sx={{
                textTransform: "none",
                py: 1.5,
                px: 4,
                fontSize: "1rem",
              }}
            >
              Select Client
            </Button>
          </Paper>
        </Container>
      
    );
  }

  return (
    <Box>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
              mb: 2,
            }}
          >
          
            <Chip
              icon={<PersonAddIcon />}
              label={`Client: ${
                activeClient?.username ||
                selectedClient?.username ||
                activeClient?.name ||
                selectedClient?.name
              }`}
              color="primary"
              variant="outlined"
              sx={{ px: 1, py: 2.5 }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            Manage and view all companies associated with the selected client
          </Typography>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 4, borderRadius: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<PersonAddIcon />}
                onClick={handleSelectClient}
                sx={{ textTransform: "none" }}
              >
                Select Client
              </Button>
            }
          >
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Unable to Load Company Data
            </Typography>
            <Typography variant="body2">{error}</Typography>
          </Alert>
        )}

        {/* No Companies Alert */}
        {!error && kycData.length === 0 && (
          <Paper
            elevation={2}
            sx={{
              p: 4,
              textAlign: "center",
              borderRadius: 2,
              backgroundColor: "info.lighter",
            }}
          >
            <BusinessIcon sx={{ fontSize: 60, color: "info.main", mb: 2 }} />
            <Typography variant="h6" gutterBottom fontWeight={500}>
              No Companies Found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This client doesn't have any registered companies yet.
            </Typography>
          </Paper>
        )}

        {/* Companies Grid with Carousel */}
        {!error && kycData.length > 0 && (
          <Box sx={{ position: "relative" }}>
            {/* Page Counter */}
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <Typography variant="h6" fontWeight={600} color="text.secondary">
                Showing {currentPage * ITEMS_PER_PAGE + 1} - {Math.min((currentPage + 1) * ITEMS_PER_PAGE, kycData.length)} of {kycData.length} Companies
              </Typography>
            </Box>

            {/* Navigation and Grid Container */}
            <CarouselContainer>
              {/* Previous Button */}
              <NavigationButton
                onClick={handlePrevious}
                disabled={currentPage === 0}
                sx={{ left: 0 }}
              >
                <ChevronLeftIcon fontSize="large" />
              </NavigationButton>

              {/* Companies Grid */}
              <Grid container spacing={3}>
                {getCurrentPageCompanies().map((company) => (
                  <Grid item xs={12} sm={6} md={4} key={company._id}>
                    <StyledCompanyCard selected={selectedCompanyId === company._id}>
                      {selectedCompanyId === company._id && (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Selected"
                          color="primary"
                          size="small"
                          sx={{
                            position: "absolute",
                            top: 12,
                            right: 12,
                            fontWeight: 600,
                            zIndex: 1,
                          }}
                        />
                      )}

                      <CardContent sx={{ p: 3, flexGrow: 1, display: "flex", flexDirection: "column" }}>
                        <Typography
                          variant="h6"
                          component="h2"
                          gutterBottom
                          fontWeight={600}
                          color="primary"
                          sx={{ mb: 2, pr: selectedCompanyId === company._id ? 8 : 0 }}
                        >
                          {company.companyInfo?.legalName || "N/A"}
                        </Typography>

                        <Box sx={{ mb: 2, flexGrow: 1 }}>
                          <InfoRow sx={{ mb: 1.5 }}>
                            <BusinessIcon color="action" fontSize="small" />
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Registration Number
                              </Typography>
                              <Typography variant="body2" fontWeight={500}>
                                {company.companyInfo?.registrationNumber || "N/A"}
                              </Typography>
                            </Box>
                          </InfoRow>

                          

                         
                        </Box>

                        <Button
                          variant={
                            selectedCompanyId === company._id
                              ? "contained"
                              : "outlined"
                          }
                          color="primary"
                          size="medium"
                          fullWidth
                          onClick={() => handleCompanySelect(company)}
                          startIcon={
                            selectedCompanyId === company._id ? (
                              <CheckCircleIcon />
                            ) : null
                          }
                          sx={{
                            textTransform: "none",
                            py: 1,
                            mt: "auto",
                          }}
                        >
                          {selectedCompanyId === company._id
                            ? "Selected"
                            : "Select Company"}
                        </Button>
                      </CardContent>
                    </StyledCompanyCard>
                  </Grid>
                ))}
              </Grid>

              {/* Next Button */}
              <NavigationButton
                onClick={handleNext}
                disabled={currentPage === totalPages - 1}
                sx={{ right: 0 }}
              >
                <ChevronRightIcon fontSize="large" />
              </NavigationButton>
            </CarouselContainer>

            {/* Dots Indicator */}
            {totalPages > 1 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 1,
                  mt: 4,
                }}
              >
                {Array.from({ length: totalPages }).map((_, index) => (
                  <Box
                    key={index}
                    onClick={() => setCurrentPage(index)}
                    sx={{
                      width: currentPage === index ? 24 : 10,
                      height: 10,
                      borderRadius: 5,
                      backgroundColor:
                        currentPage === index ? "primary.main" : "grey.300",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        backgroundColor: currentPage === index ? "primary.dark" : "grey.400",
                      },
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default MyClientCompany;
